/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { createServiceToken } from "@workglow/util";
import { DataPortSchemaObject, FromSchema, TypedArraySchemaOptions } from "@workglow/util/schema";
import { type ITabularMigration, type ITabularMigrationApplier } from "../migrations";
import { BaseTabularStorage } from "./BaseTabularStorage";
import { decodeCursor, encodeCursor, PageCursor } from "./Cursor";
import { InMemoryTabularMigrationApplier } from "./InMemoryTabularMigrationApplier";
import {
  AnyTabularStorage,
  AutoGeneratedKeys,
  DeleteSearchCriteria,
  InsertEntity,
  isSearchCondition,
  Page,
  PageRequest,
  QueryOptions,
  SearchCriteria,
  SimplifyPrimaryKey,
  TabularChangePayload,
  TabularSubscribeOptions,
} from "./ITabularStorage";
import { StorageUnsupportedError, StorageValidationError } from "./StorageError";

export const HF_TABULAR_REPOSITORY = createServiceToken<AnyTabularStorage>(
  "storage.tabularRepository.huggingface"
);

/**
 * Cursor for HuggingFace pagination. Encodes a numeric offset into the
 * standard cursor format (so callers always see opaque strings) but uses
 * the offset directly against the HF `/rows` endpoint, which doesn't
 * support tuple comparisons. Stability under concurrent writes is moot
 * because HF datasets are read-only.
 *
 * The synthetic `hfOffset` "column name" makes the cursor self-describing
 * for `decodeCursor`'s n/c arity check. HF's offset paging doesn't
 * actually reference a column, but the cursor format requires names
 * alongside values. The chosen identifier satisfies the schema
 * column-name regex (`^[a-zA-Z][a-zA-Z0-9_]*$`) so the cursor would pass
 * schema-membership validation if that ever broadens.
 */
const HF_OFFSET_CURSOR_NAME = "hfOffset";

function encodeOffsetCursor(offset: number): PageCursor {
  return encodeCursor({ v: 1, n: [HF_OFFSET_CURSOR_NAME], d: ["a"], c: [offset] });
}

function decodeOffsetCursor(cursor: PageCursor | string): number {
  // Use `StorageValidationError` (not generic `Error`) so callers that
  // surface decode failures as 4xx responses can catch one consistent
  // error type across all backends — matches how `decodeCursor` itself
  // and every other cursor validator in the storage package behave.
  const payload = decodeCursor(cursor);
  if (payload.n[0] !== HF_OFFSET_CURSOR_NAME) {
    throw new StorageValidationError("Cursor was not produced by HuggingFaceTabularStorage");
  }
  const value = payload.c[0];
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new StorageValidationError("Invalid HuggingFace pagination cursor");
  }
  return value;
}

interface HfFirstRowsResponse {
  features: Array<{ feature_idx: number; name: string; type: any }>;
  rows: Array<{ row_idx: number; row: Record<string, any>; truncated_cells: any[] }>;
}

interface HfRowsResponse {
  features: Array<{ feature_idx: number; name: string; type: any }>;
  rows: Array<{ row_idx: number; row: Record<string, any>; truncated_cells: any[] }>;
  num_rows_total: number;
  num_rows_per_page: number;
  partial: boolean;
}

interface HfFilterResponse {
  features: Array<{ feature_idx: number; name: string; type: any }>;
  rows: Array<{ row_idx: number; row: Record<string, any>; truncated_cells: any[] }>;
  num_rows_total: number;
  num_rows_per_page: number;
  partial: boolean;
}

interface HfSizeResponse {
  size: {
    dataset: string;
    config: string;
    split: string;
    num_bytes_original_files: number;
    num_bytes_parquet_files: number;
    num_bytes_memory: number;
    num_rows: number;
    num_columns: number;
  };
  partial: boolean;
}

export interface HuggingFaceTabularStorageOptions {
  /** HuggingFace API token for private datasets. */
  token?: string;
  /** Base URL for the HuggingFace Dataset Viewer API. */
  baseUrl?: string;
  indexes?: readonly (keyof any | readonly (keyof any)[])[];
}

/**
 * Read-only tabular storage backed by the HuggingFace Dataset Viewer API.
 * Supports both user-provided schemas and auto-detection via {@link fromDataset}.
 */
export class HuggingFaceTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  // computed types
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  Value = Omit<Entity, PrimaryKeyNames[number] & keyof Entity>,
  InsertType extends InsertEntity<Entity, AutoGeneratedKeys<Schema>> = InsertEntity<
    Entity,
    AutoGeneratedKeys<Schema>
  >,
> extends BaseTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, Value, InsertType> {
  private readonly dataset: string;
  private readonly config: string;
  private readonly split: string;
  private readonly token?: string;
  private readonly baseUrl: string;

  constructor(
    dataset: string,
    config: string,
    split: string,
    schema: Schema,
    primaryKeyNames: PrimaryKeyNames,
    options?: HuggingFaceTabularStorageOptions,
    tabularMigrations?: ReadonlyArray<ITabularMigration>
  ) {
    super(
      schema,
      primaryKeyNames,
      (options?.indexes ?? []) as readonly (
        | keyof NoInfer<Entity>
        | readonly (keyof NoInfer<Entity>)[]
      )[],
      "never", // HF datasets don't support client-provided keys.
      tabularMigrations,
      `hf:${dataset}/${config}/${split}`
    );
    this.dataset = dataset;
    this.config = config;
    this.split = split;
    this.token = options?.token;
    this.baseUrl = options?.baseUrl ?? "https://datasets-server.huggingface.co";
  }

  /** Fetches the dataset features and converts them to a JSON Schema. */
  static async fromDataset<Entity = any>(
    dataset: string,
    config: string,
    split: string,
    options?: HuggingFaceTabularStorageOptions
  ): Promise<
    HuggingFaceTabularStorage<DataPortSchemaObject, readonly ["row_idx"], Entity, number, any, any>
  > {
    const baseUrl = options?.baseUrl ?? "https://datasets-server.huggingface.co";
    const token = options?.token;

    const url = new URL(`${baseUrl}/first-rows`);
    url.searchParams.set("dataset", dataset);
    url.searchParams.set("config", config);
    url.searchParams.set("split", split);

    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url.toString(), { headers });
    if (!response.ok) {
      throw new Error(
        `Failed to fetch dataset features: ${response.status} ${response.statusText}`
      );
    }

    const data: HfFirstRowsResponse = await response.json();

    const properties: Record<string, any> = {};
    const required: string[] = [];

    // `row_idx` is HF's row identifier and is always auto-assigned by the API.
    properties["row_idx"] = { type: "integer", "x-auto-generated": true };
    required.push("row_idx");

    for (const feature of data.features) {
      const jsonSchema = hfFeatureToJsonSchema(feature.type);
      properties[feature.name] = jsonSchema;
      required.push(feature.name);
    }

    const schema: DataPortSchemaObject = {
      type: "object",
      properties,
      required,
      additionalProperties: false,
    };

    return new HuggingFaceTabularStorage(
      dataset,
      config,
      split,
      schema,
      ["row_idx"] as const,
      options
    ) as any;
  }

  /** Validates the dataset exists and that any user-provided schema lines up. */
  override async setupDatabase(): Promise<void> {
    const data = await this.fetchApi<HfFirstRowsResponse>("/first-rows", {});

    const schemaColumns = Object.keys(this.schema.properties);
    const hfColumns = data.features.map((f) => f.name);

    // Presence of `row_idx` signals auto-detected schema; skip user-schema checks.
    const hasRowIdx = schemaColumns.includes("row_idx");

    if (!hasRowIdx) {
      for (const column of schemaColumns) {
        if (!hfColumns.includes(column) && !this.primaryKeyNames.includes(column as any)) {
          throw new Error(`Schema column "${column}" not found in HuggingFace dataset features`);
        }
      }
    }

    if (this.tabularMigrations && this.tabularMigrations.length > 0) {
      await this.applyTabularMigrations();
    }
  }

  /**
   * Returns an in-memory applier for HF tabular storage. NOTE: HF datasets
   * are read-only; backfill ops will throw because `put` is unsupported.
   * DDL ops are no-ops (records are JS objects). Migrations on HF storages
   * are useful only for advancing bookkeeping in lockstep with a producer
   * that re-publishes datasets.
   */
  public override getMigrationApplier(): ITabularMigrationApplier | null {
    return new InMemoryTabularMigrationApplier(
      this as unknown as AnyTabularStorage,
      `hf:${this.dataset}/${this.config}/${this.split}`
    );
  }

  async get(key: PrimaryKey): Promise<Entity | undefined> {
    const keyObj = this.separateKeyValueFromCombined({ ...key } as any).key;
    const whereConditions: string[] = [];

    for (const [k, v] of Object.entries(keyObj as Record<string, any>)) {
      if (typeof v === "string") {
        // Escape backslashes first, then single quotes.
        const escaped = v.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
        whereConditions.push(`${k}='${escaped}'`);
      } else {
        whereConditions.push(`${k}=${v}`);
      }
    }

    const where = whereConditions.join(" AND ");
    const data = await this.fetchApi<HfFilterResponse>("/filter", { where, limit: "1" });

    if (data.rows.length > 0) {
      const entity = this.rowToEntity(data.rows[0]);
      this.events.emit("get", key, entity);
      return entity;
    }

    this.events.emit("get", key, undefined);
    return undefined;
  }

  async getAll(options?: QueryOptions<Entity>): Promise<Entity[] | undefined> {
    this.validateGetAllOptions(options);

    const allEntities: Entity[] = [];
    let offset = 0;
    const pageSize = 100; // HF max per request.

    while (true) {
      const page = await this.getOffsetPage(offset, pageSize);

      if (!page || page.length === 0) {
        break;
      }

      allEntities.push(...page);
      offset += page.length;

      if (page.length < pageSize) {
        break;
      }
    }

    if (allEntities.length === 0) return undefined;

    let results = allEntities;

    if (options?.orderBy && options.orderBy.length > 0) {
      results = [...results];
      results.sort((a, b) => {
        for (const { column, direction } of options.orderBy!) {
          const aVal = a[column] as string | number | null | undefined;
          const bVal = b[column] as string | number | null | undefined;
          if (aVal == null && bVal == null) continue;
          if (aVal == null) return direction === "ASC" ? -1 : 1;
          if (bVal == null) return direction === "ASC" ? 1 : -1;
          if (aVal < bVal) return direction === "ASC" ? -1 : 1;
          if (aVal > bVal) return direction === "ASC" ? 1 : -1;
        }
        return 0;
      });
    }

    if (options?.offset !== undefined) {
      results = results.slice(options.offset);
    }

    if (options?.limit !== undefined) {
      results = results.slice(0, options.limit);
    }

    return results.length > 0 ? results : undefined;
  }

  async getOffsetPage(offset: number, limit: number): Promise<Entity[] | undefined> {
    const data = await this.fetchApi<HfRowsResponse>("/rows", {
      offset: offset.toString(),
      length: Math.min(limit, 100).toString(), // HF caps at 100 per request.
    });

    if (data.rows.length === 0) {
      return undefined;
    }

    const entities: Entity[] = [];
    for (const row of data.rows) {
      entities.push(this.rowToEntity(row));
    }

    return entities;
  }

  /**
   * HuggingFace datasets are read-only, so the concurrency-stability that
   * keyset pagination provides is unnecessary. The HF API also doesn't
   * expose tuple comparisons, so we drive cursor pagination from the
   * `/rows` endpoint's offset and encode the next offset in the cursor.
   *
   * The HF `/rows` endpoint caps each fetch at 100 rows, but the
   * {@link ITabularStorage.getPage} contract lets callers ask for any
   * positive limit. Loop in 100-row chunks until we either fill the
   * caller's `limit` or hit the end of the dataset, so a `getPage({ limit:
   * 200 })` doesn't silently return only 100 rows with a `nextCursor` of
   * `undefined` (which would terminate iteration despite more data).
   */
  override async getPage(request: PageRequest<Entity> = {}): Promise<Page<Entity>> {
    this.validatePageRequest(request);
    const limit = request.limit ?? 100;
    if (request.orderBy && request.orderBy.length > 0) {
      // The HF /rows endpoint returns rows in `row_idx` order with no
      // alternative; silently overriding the caller's choice would be a
      // correctness bug, so reject it explicitly instead.
      throw new StorageUnsupportedError("orderBy in getPage", "HuggingFaceTabularStorage");
    }
    const HF_PAGE_CAP = 100;
    let offset = request.cursor ? decodeOffsetCursor(request.cursor) : 0;
    const items: Entity[] = [];
    let endOfDataset = false;
    while (items.length < limit) {
      const remaining = limit - items.length;
      const chunkSize = Math.min(remaining, HF_PAGE_CAP);
      const rows = (await this.getOffsetPage(offset, chunkSize)) ?? [];
      if (rows.length === 0) {
        endOfDataset = true;
        break;
      }
      items.push(...rows);
      offset += rows.length;
      // A short response from the HF API means the dataset has no more
      // rows for this offset window — bail out rather than spinning.
      if (rows.length < chunkSize) {
        endOfDataset = true;
        break;
      }
    }
    const nextCursor = endOfDataset ? undefined : encodeOffsetCursor(offset);
    return { items, nextCursor };
  }

  async size(): Promise<number> {
    const data = await this.fetchApi<HfSizeResponse>("/size", {});
    return data.size.num_rows;
  }

  async put(_value: InsertType): Promise<Entity> {
    throw new Error("HuggingFaceTabularStorage is readonly");
  }

  async putBulk(_values: InsertType[]): Promise<Entity[]> {
    throw new Error("HuggingFaceTabularStorage is readonly");
  }

  async delete(_value: PrimaryKey | Entity): Promise<void> {
    throw new Error("HuggingFaceTabularStorage is readonly");
  }

  async deleteAll(): Promise<void> {
    throw new Error("HuggingFaceTabularStorage is readonly");
  }

  /** The HF /filter endpoint supports equality only; other operators throw. */
  async query(
    criteria: SearchCriteria<Entity>,
    options?: QueryOptions<Entity>
  ): Promise<Entity[] | undefined> {
    this.validateQueryParams(criteria, options);

    const whereConditions: string[] = [];
    for (const [k, v] of Object.entries(criteria)) {
      if (v === undefined || v === null) continue;
      if (isSearchCondition(v)) {
        if (v.operator !== "=") {
          throw new StorageUnsupportedError(
            `Operator "${v.operator}" in query`,
            "HuggingFaceTabularStorage"
          );
        }
        const val = v.value;
        if (typeof val === "string") {
          const escaped = val.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
          whereConditions.push(`${k}='${escaped}'`);
        } else {
          whereConditions.push(`${k}=${val}`);
        }
      } else {
        if (typeof v === "string") {
          const escaped = v.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
          whereConditions.push(`${k}='${escaped}'`);
        } else {
          whereConditions.push(`${k}=${v}`);
        }
      }
    }

    if (whereConditions.length === 0) {
      return undefined;
    }

    const where = whereConditions.join(" AND ");
    const allEntities: Entity[] = [];
    let fetchOffset = 0;
    const fetchLimit = 100;

    while (true) {
      const data = await this.fetchApi<HfFilterResponse>("/filter", {
        where,
        offset: fetchOffset.toString(),
        limit: fetchLimit.toString(),
      });

      for (const row of data.rows) {
        allEntities.push(this.rowToEntity(row));
      }

      fetchOffset += data.rows.length;

      if (fetchOffset >= data.num_rows_total || data.rows.length < fetchLimit) {
        break;
      }
    }

    let results = allEntities;

    if (options?.orderBy && options.orderBy.length > 0) {
      results.sort((a, b) => {
        for (const { column, direction } of options.orderBy!) {
          const aVal = a[column] as string | number | null | undefined;
          const bVal = b[column] as string | number | null | undefined;
          if (aVal == null && bVal == null) continue;
          if (aVal == null) return direction === "ASC" ? -1 : 1;
          if (bVal == null) return direction === "ASC" ? 1 : -1;
          if (aVal < bVal) return direction === "ASC" ? -1 : 1;
          if (aVal > bVal) return direction === "ASC" ? 1 : -1;
        }
        return 0;
      });
    }

    if (options?.offset !== undefined) {
      results = results.slice(options.offset);
    }

    if (options?.limit !== undefined) {
      results = results.slice(0, options.limit);
    }

    if (results.length > 0) {
      this.events.emit("query", criteria as Partial<Entity>, results);
      return results;
    } else {
      this.events.emit("query", criteria as Partial<Entity>, undefined);
      return undefined;
    }
  }

  async deleteSearch(_criteria: DeleteSearchCriteria<Entity>): Promise<void> {
    throw new Error("HuggingFaceTabularStorage is readonly");
  }

  override subscribeToChanges(
    _callback: (change: TabularChangePayload<Entity>) => void,
    _options?: TabularSubscribeOptions
  ): () => void {
    throw new Error("HuggingFaceTabularStorage does not support subscriptions");
  }

  override destroy(): void {}

  private async fetchApi<T>(endpoint: string, params: Record<string, string>): Promise<T> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    url.searchParams.set("dataset", this.dataset);
    url.searchParams.set("config", this.config);
    url.searchParams.set("split", this.split);

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, value);
      }
    }

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const response = await fetch(url.toString(), { headers });
    if (!response.ok) {
      throw new Error(`HuggingFace API error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  private rowToEntity(row: { row_idx: number; row: Record<string, any> }): Entity {
    return { row_idx: row.row_idx, ...row.row } as Entity;
  }
}

function hfFeatureToJsonSchema(feature: any): any {
  if (feature._type === "Value") {
    switch (feature.dtype) {
      case "string":
        return { type: "string" };
      case "int64":
      case "int32":
      case "int16":
      case "int8":
      case "uint64":
      case "uint32":
      case "uint16":
      case "uint8":
        return { type: "integer" };
      case "float64":
      case "float32":
      case "float16":
        return { type: "number" };
      case "bool":
        return { type: "boolean" };
      default:
        return {};
    }
  }

  if (feature._type === "ClassLabel") {
    return { type: "integer" };
  }

  if (feature._type === "Sequence") {
    return {
      type: "array",
      items: hfFeatureToJsonSchema(feature.feature),
    };
  }

  return {};
}
